import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Megaphone, Upload, X } from "lucide-react";
import FolderPicker from "@/components/FolderPicker";
import { useAuth } from "@/contexts/AuthContext";

const SCOPE_OPTIONS = [
  { v: "", l: "Ei rajata (oletus)" },
  { v: "all_units", l: "Koskee kaikkia asuntoja" },
  { v: "some_units", l: "Koskee osaa asunnoista" },
  { v: "common_areas", l: "Yleisiä tiloja" },
  { v: "outdoor", l: "Ulkoaluetta" },
];

const DEFAULT_INSTRUCTION_PRESETS = [
  "Jätä turvalukko auki",
  "Varaa esteetön pääsy",
  "Siirrä tavarat pois työalueelta",
  "Lemmikit pois työalueelta",
  "Ole kotona sovittuna aikana",
];

/**
 * buildDefaultDescription — luo asukastiedotteen oletuskuvausteksti käyttäjän
 * Asetuksien perusteella. Sähköposti ja puhelin sijoitetaan omalle riville
 * kaksoispisteen jälkeen. Jos arvo puuttuu Asetuksista, jätetään tyhjä rivi
 * johon käyttäjä voi kirjoittaa tiedotekohtaisesti. EI hardcoded-placeholdereita
 * (ei "000", ei "info@veraso.fi").
 */
function buildDefaultDescription({ email = "", phone = "" } = {}) {
  const emailLine = email.trim() ? `\n${email.trim()}` : "";
  const phoneLine = phone.trim() ? `\n${phone.trim()}` : "";
  return (
    `Mikäli teillä herää jotain kysymyksiä tai huomioita tulevaan käyntiin liittyen, olettehan yhteydessä sähköpostitse osoitteeseen:${emailLine}\n\n` +
    `tai soittamalla asiakaspalveluun numeroon:${phoneLine}\n\n` +
    `Kun olette meihin yhteydessä, muistattehan ilmoittaa mitä osoitetta ja huoneistoa ilmoitus koskee sekä jättää yhteystietonne (nimi, osoite, puhelinnumero, sähköpostiosoite) mahdollista yhteydenottoa varten.\n\n` +
    `• Mahdolliset turvalukot tulee jättää avoimiksi ilmoitettuna aikavälinä.\n` +
    `• Käytössämme on yleisavain, eikä työn tekeminen edellytä asukkaiden läsnäoloa.\n` +
    `• Huoltotöiden aikana lemmikkien tulee olla pois huoneistosta tai omistajan valvonnassa.`
  );
}

const empty = () => ({
  title: "",
  template_id: null,
  company_name: "",
  company_logo: null,
  company_phone: "",
  company_email: "",
  company_website: "",
  site_name: "",
  site_address: "",
  site_city: "",
  site_section: "",
  customer_name: "",
  customer_contact: "",
  customer_contact_info: "",
  work_summary: "",
  work_description: "",  // täytetään useEffectissä käyttäjän Asetuksien pohjalta
  scope: "",
  date: "",
  date_end: "",
  time_start: "",
  time_end: "",
  backup_date: "",
  impacts: [],
  instructions: [],
  extra_info: "",
  signed_by: "",
  signed_company: "",
  signature_closing: "Ystävällisin terveisin",
  folder_id: null,
});

export default function NoticeWizard() {
  const { noticeId } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(noticeId);
  const { user } = useAuth();

  const [data, setData] = useState(empty());
  const [presets, setPresets] = useState([]);
  const [impactLabels, setImpactLabels] = useState({});
  const [busy, setBusy] = useState(false);
  const [customInstr, setCustomInstr] = useState("");
  const [customImpact, setCustomImpact] = useState("");

  const addCustomImpact = () => {
    const t = customImpact.trim();
    if (!t) return;
    setData((d) => ({ ...d, impacts: d.impacts.includes(t) ? d.impacts : [...d.impacts, t] }));
    setCustomImpact("");
  };

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setData((d) => ({ ...d, [k]: v }));
  };

  useEffect(() => {
    api.get("/catalog/notice-templates").then(({ data }) => {
      setPresets(data.templates);
      setImpactLabels(data.impact_labels);
    });
    if (editing) {
      api.get(`/notices/${noticeId}`).then(({ data }) => setData({ ...empty(), ...data })).catch(() => {
        toast.error("Tiedotetta ei löytynyt");
        navigate("/app");
      });
    }
  }, [noticeId, editing, navigate]);

  // Esitäytä yritystiedot ja oletuskuvausteksti Asetuksista (uudelle tiedotteelle).
  // Käyttäjän ei tarvitse kirjoittaa yrityksen nimeä/logoa/yhteystietoja joka
  // tiedotteeseen — ne tulevat suoraan tililtä. Sähköposti ja puhelin
  // sijoitetaan myös kuvauksen "yhteydessä"-osioon automaattisesti. Käyttäjä
  // voi silti muokata mitä tahansa arvoa manuaalisesti.
  useEffect(() => {
    if (editing || !user) return;
    setData((d) => ({
      ...d,
      company_name: d.company_name || user.company_name || "",
      company_logo: d.company_logo || user.company_logo || null,
      company_phone: d.company_phone || user.company_phone || "",
      company_email: d.company_email || user.company_email || "",
      company_website: d.company_website || user.company_website || "",
      work_description: d.work_description || buildDefaultDescription({
        email: user.company_email || "",
        phone: user.company_phone || "",
      }),
    }));
  }, [editing, user]);

  // Dynamic browser/PDF document title — drives default PDF filename
  // when user prints from this editor view (Cmd+P / Tulosta).
  useEffect(() => {
    if (!editing) return;
    const label = (data.site_name || data.title || "").trim();
    const prev = document.title;
    document.title = label ? `Asukastiedote - ${label}` : "Asukastiedote";
    return () => { document.title = prev; };
  }, [editing, data.site_name, data.title]);

  const applyPreset = (p) => {
    setData((d) => ({
      ...d,
      template_id: p.id,
      title: d.title || p.title_template,
      work_summary: d.work_summary || p.work_summary,
      impacts: Array.from(new Set([...(d.impacts || []), ...(p.impacts || [])])),
      instructions: Array.from(new Set([...(d.instructions || []), ...(p.instructions || [])])),
    }));
    toast.success(`Pohja "${p.label}" ladattu`);
  };

  const toggleImpact = (key) => {
    setData((d) => ({
      ...d,
      impacts: d.impacts.includes(key) ? d.impacts.filter((x) => x !== key) : [...d.impacts, key],
    }));
  };

  const toggleInstruction = (txt) => {
    setData((d) => ({
      ...d,
      instructions: d.instructions.includes(txt) ? d.instructions.filter((x) => x !== txt) : [...d.instructions, txt],
    }));
  };

  const addCustomInstr = () => {
    const txt = customInstr.trim();
    if (!txt) return;
    setData((d) => ({ ...d, instructions: [...d.instructions, txt] }));
    setCustomInstr("");
  };

  const removeInstruction = (txt) => {
    setData((d) => ({ ...d, instructions: d.instructions.filter((x) => x !== txt) }));
  };

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo max 2 MB"); return; }
    const compressed = await compressImageDataURL(file);
    setData((d) => ({ ...d, company_logo: compressed }));
  };

  const submit = async () => {
    if (!data.title.trim()) { toast.error("Anna tiedotteelle otsikko"); return; }
    setBusy(true);
    // Backend's Optional[Literal[...]] rejects empty string for `scope` —
    // convert "" to null before sending so "Ei rajata" works.
    const payload = { ...data, scope: data.scope || null };
    try {
      if (editing) {
        const { data: res } = await api.patch(`/notices/${noticeId}`, payload);
        toast.success("Tiedote päivitetty");
        navigate(`/app/asukastiedote/${res.notice_id}`);
      } else {
        const { data: res } = await api.post("/notices", payload);
        toast.success("Tiedote luotu");
        navigate(`/app/asukastiedote/${res.notice_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tallennus epäonnistui");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-8">
        <button onClick={() => navigate("/app")} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-4" data-testid="notice-back">
          <ArrowLeft className="h-4 w-4" /> Takaisin
        </button>

        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-emerald-600 font-semibold inline-flex items-center gap-1.5">
            <Megaphone className="h-3.5 w-3.5" /> {editing ? "Muokkaa tiedotetta" : "Uusi asukastiedote"}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mt-1">
            Luo siisti tiedote muutamassa minuutissa
          </h1>
          <p className="text-slate-600 mt-2">Täytä tiedot. Esikatselu ja tulostus seuraavalla sivulla.</p>
        </div>

        <Section title="Yritystiedot" subtitle="Nämä tiedot näkyvät tiedotteen ylälaidassa.">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Yrityksen nimi" required>
              <input className="input-base" value={data.company_name} onChange={set("company_name")} data-testid="notice-company-name" />
            </Field>
            <Field label="Logo">
              <div className="flex items-center gap-3">
                {data.company_logo ? (
                  <div className="relative">
                    <img src={data.company_logo} alt="" className="h-12 max-w-[160px] object-contain rounded border border-slate-200 bg-white p-1" />
                    <button onClick={() => setData((d) => ({ ...d, company_logo: null }))} className="absolute -top-2 -right-2 bg-white rounded-full p-0.5 border border-slate-300 shadow-sm" data-testid="notice-logo-remove">
                      <X className="h-3 w-3 text-slate-600" />
                    </button>
                  </div>
                ) : (
                  <label className="btn-ghost text-sm cursor-pointer" data-testid="notice-logo-upload">
                    <Upload className="h-4 w-4" /> Lataa logo
                    <input type="file" accept="image/*" hidden onChange={onLogoFile} />
                  </label>
                )}
              </div>
            </Field>
            <Field label="Puhelinnumero">
              <input className="input-base" value={data.company_phone} onChange={set("company_phone")} data-testid="notice-company-phone" />
            </Field>
            <Field label="Sähköposti">
              <input className="input-base" type="email" value={data.company_email} onChange={set("company_email")} data-testid="notice-company-email" />
            </Field>
            <Field label="Nettisivu (valinnainen)">
              <input className="input-base" value={data.company_website} onChange={set("company_website")} data-testid="notice-company-website" />
            </Field>
          </div>
        </Section>

        <Section title="2. Kohteen tiedot">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Kohteen nimi">
              <input className="input-base" value={data.site_name} onChange={set("site_name")} placeholder="As Oy Kirjokuja" data-testid="notice-site-name" />
            </Field>
            <Field label="Osoite">
              <input className="input-base" value={data.site_address} onChange={set("site_address")} placeholder="Kirjokuja 4" data-testid="notice-site-address" />
            </Field>
            <Field label="Kaupunki">
              <input className="input-base" value={data.site_city} onChange={set("site_city")} placeholder="Helsinki" data-testid="notice-site-city" />
            </Field>
            <Field label="Rappu / alue (valinnainen)">
              <input className="input-base" value={data.site_section} onChange={set("site_section")} placeholder="A-rappu" data-testid="notice-site-section" />
            </Field>
          </div>
        </Section>

        <Section title="3. Tilaaja (valinnainen)">
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Tilaaja / taloyhtiö / isännöitsijä">
              <input className="input-base" value={data.customer_name} onChange={set("customer_name")} data-testid="notice-customer-name" />
            </Field>
            <Field label="Yhteyshenkilö">
              <input className="input-base" value={data.customer_contact} onChange={set("customer_contact")} data-testid="notice-customer-contact" />
            </Field>
            <Field label="Yhteystiedot">
              <input className="input-base" value={data.customer_contact_info} onChange={set("customer_contact_info")} data-testid="notice-customer-contact-info" />
            </Field>
          </div>
        </Section>

        <Section title="4. Työn tiedot" subtitle="Kerro lyhyesti mitä tehdään ja keitä se koskee.">
          <Field label="Otsikko" required>
            <input className="input-base" value={data.title} onChange={set("title")} placeholder="Tiedote: Putkityö" data-testid="notice-title" />
          </Field>
          <Field label="Tarkempi kuvaus (valinnainen)">
            <textarea rows={6} className="input-base" value={data.work_description} onChange={set("work_description")} data-testid="notice-work-description" />
          </Field>
          <Field label="Koskee">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {SCOPE_OPTIONS.map((s) => (
                <label key={s.v} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 cursor-pointer text-sm ${data.scope === s.v ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
                  <input type="radio" name="scope" value={s.v} checked={data.scope === s.v} onChange={() => setData({ ...data, scope: s.v })} data-testid={`notice-scope-${s.v}`} />
                  <span>{s.l}</span>
                </label>
              ))}
            </div>
          </Field>
        </Section>

        <Section title="5. Ajankohta">
          <div className="grid md:grid-cols-4 gap-4">
            <Field label="Aloituspäivä">
              <DateInputFI value={data.date} onChange={(v) => setData((d) => ({ ...d, date: v }))} testId="notice-date" />
            </Field>
            <Field label="Päättymispäivä (valinnainen)">
              <DateInputFI value={data.date_end} onChange={(v) => setData((d) => ({ ...d, date_end: v }))} testId="notice-date-end" />
            </Field>
            <Field label="Aloituskellonaika">
              <TimeInput24 value={data.time_start} onChange={(v) => setData((d) => ({ ...d, time_start: v }))} testId="notice-time-start" />
            </Field>
            <Field label="Lopetuskellonaika">
              <TimeInput24 value={data.time_end} onChange={(v) => setData((d) => ({ ...d, time_end: v }))} testId="notice-time-end" />
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Varapäivä (valinnainen)">
              <DateInputFI value={data.backup_date} onChange={(v) => setData((d) => ({ ...d, backup_date: v }))} testId="notice-backup-date" />
            </Field>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Jos vain aloituspäivä on annettu, tiedote näyttää yksittäisen päivän. Päivämäärät näkyvät Suomi-muodossa (pp.kk.vvvv), ja kellonajat 24 h.
          </p>
        </Section>

        <Section title="6. Vaikutukset asukkaille">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(impactLabels).map(([k, l]) => (
              <label key={k} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer ${data.impacts.includes(k) ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`} data-testid={`notice-impact-${k}`}>
                <input type="checkbox" className="h-4 w-4" checked={data.impacts.includes(k)} onChange={() => toggleImpact(k)} />
                <span className="text-sm">{l}</span>
              </label>
            ))}
          </div>

          {data.impacts.filter((x) => !impactLabels[x]).length > 0 && (
            <div className="mt-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Omat vaikutukset</div>
              <div className="space-y-1">
                {data.impacts.filter((x) => !impactLabels[x]).map((txt) => (
                  <div key={txt} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
                    <span className="text-sm text-slate-800 flex-1">{txt}</span>
                    <button onClick={() => setData((d) => ({ ...d, impacts: d.impacts.filter((v) => v !== txt) }))} className="p-1 rounded text-slate-400 hover:text-red-600">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <input
              className="input-base flex-1"
              placeholder="Lisää oma vaikutus..."
              value={customImpact}
              onChange={(e) => setCustomImpact(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomImpact())}
              data-testid="notice-impact-custom-input"
            />
            <button type="button" onClick={addCustomImpact} disabled={!customImpact.trim()} className="btn-primary px-4 disabled:opacity-50" data-testid="notice-impact-add">
              Lisää
            </button>
          </div>
        </Section>

        <Section title="7. Ohjeet asukkaille">
          <div className="space-y-1.5">
            {DEFAULT_INSTRUCTION_PRESETS.map((s) => (
              <label key={s} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer ${data.instructions.includes(s) ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`} data-testid={`notice-instr-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                <input type="checkbox" className="h-4 w-4" checked={data.instructions.includes(s)} onChange={() => toggleInstruction(s)} />
                <span className="text-sm">{s}</span>
              </label>
            ))}
          </div>
          {data.instructions.filter((x) => !DEFAULT_INSTRUCTION_PRESETS.includes(x)).length > 0 && (
            <div className="mt-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Omat ohjeet</div>
              <div className="space-y-1">
                {data.instructions.filter((x) => !DEFAULT_INSTRUCTION_PRESETS.includes(x)).map((txt) => (
                  <div key={txt} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
                    <span className="text-sm text-slate-800 flex-1">{txt}</span>
                    <button onClick={() => removeInstruction(txt)} className="p-1 rounded text-slate-400 hover:text-red-600">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <input
              className="input-base flex-1"
              placeholder="Lisää oma ohje..."
              value={customInstr}
              onChange={(e) => setCustomInstr(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomInstr())}
              data-testid="notice-instr-custom-input"
            />
            <button type="button" onClick={addCustomInstr} disabled={!customInstr.trim()} className="btn-primary px-4 disabled:opacity-50" data-testid="notice-instr-add">
              Lisää
            </button>
          </div>
        </Section>

        <Section title="8. Allekirjoitus">
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Lähettäjän nimi">
              <input className="input-base" value={data.signed_by} onChange={set("signed_by")} data-testid="notice-signed-by" />
            </Field>
            <Field label="Yrityksen nimi (allekirjoituksessa)">
              <input className="input-base" value={data.signed_company} onChange={set("signed_company")} placeholder={data.company_name} data-testid="notice-signed-company" />
            </Field>
            <Field label="Tervehdys">
              <input className="input-base" value={data.signature_closing} onChange={set("signature_closing")} data-testid="notice-signature-closing" />
            </Field>
          </div>
        </Section>

        <Section title="9. Tallennus">
          <Field label="Kohde">
            <FolderPicker
              value={data.folder_id}
              onChange={(folder_id) => setData((d) => ({ ...d, folder_id }))}
              label={null}
            />
            <p className="mt-2 text-xs text-slate-500">
              Voit liittää tiedotteen olemassa olevaan kohteeseen tai jättää kohteettomaksi.
            </p>
          </Field>
        </Section>

        <div className="flex items-center justify-between gap-3">
          <button onClick={() => navigate("/app")} className="btn-ghost" data-testid="notice-cancel">
            Peruuta
          </button>
          <button onClick={submit} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="notice-submit">
            <Save className="h-4 w-4" /> {busy ? "Tallennetaan..." : (editing ? "Tallenna muutokset" : "Luo tiedote")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="card-soft p-5 md:p-6 mb-4">
      <div className="mb-4">
        <h2 className="font-display text-lg font-semibold text-slate-900">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
    </div>
  );
}

async function compressImageDataURL(file, maxDim = 400, quality = 0.9) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) { height = Math.round((height / width) * maxDim); width = maxDim; }
          else { width = Math.round((width / height) * maxDim); height = maxDim; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/png"));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}


/**
 * TimeInput24 — 24-tunnin kellonaikakenttä joka toimii yhtenäisesti kaikilla
 * selaimilla/käyttöjärjestelmillä.
 *
 * Ongelma: HTML:n <input type="time"> käyttää selaimen/OS:n localea. Chromella
 * ja Safarilla useilla laitteilla (varsinkin jos OS on englanniksi) tämä näyttää
 * AM/PM-valitsimen. `lang="fi-FI"` ei riitä pakottamaan 24h-formaattia — se on
 * vain hint jonka selain saa ohittaa.
 *
 * Ratkaisu: käytetään text-inputtia HH:MM-maskilla. Käyttäjä syöttää joko
 * "8", "830", "8:30" tai "08:30" ja onBlur normalisoi arvon muotoon "HH:MM".
 * Arvo tallennetaan aina ISO-muodossa (24h), mikä on yhteensopiva backendin
 * kanssa (ei muutoksia tarvita).
 */
function TimeInput24({ value, onChange, testId }) {
  const [text, setText] = useState(value || "");
  useEffect(() => { setText(value || ""); }, [value]);

  const normalize = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return "";
    // Poimi ensimmäiset numerot; hyväksy erottimet : . ,
    const m = s.match(/^(\d{1,2})[.:,\s-]?(\d{0,2})/);
    if (!m) return "";
    let h = parseInt(m[1], 10);
    let mi = m[2] === "" ? 0 : parseInt(m[2], 10);
    if (Number.isNaN(h) || Number.isNaN(mi)) return "";
    if (h > 23) h = 23;
    if (mi > 59) mi = 59;
    return `${String(h).padStart(2, "0")}:${String(mi).padStart(2, "0")}`;
  };

  const commit = () => {
    const norm = normalize(text);
    setText(norm);
    if (norm !== (value || "")) onChange(norm);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      className="input-base tabular-nums"
      placeholder="HH:MM"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
      maxLength={5}
      data-testid={testId}
    />
  );
}


/**
 * DateInputFI — päivämääräkenttä Suomen muodossa (pp.kk.vvvv).
 *
 * Ongelma: HTML:n <input type="date"> käyttää selaimen/OS:n localea. Suomeksi
 * asennetuissa selaimissa placeholder on "pp.kk.vvvv", mutta jos OS on
 * englanniksi, näkyy "mm/dd/yyyy". lang-atribuutti ei riitä pakottamaan.
 *
 * Ratkaisu: text-input pp.kk.vvvv-muodossa. Käyttäjä syöttää joko "1.4.2026",
 * "01.04.2026", "1.4" tai "01042026" ja onBlur normalisoi muotoon "01.04.2026".
 * Arvo TALLENNETAAN aina ISO-muodossa (YYYY-MM-DD) → backend-yhteensopivuus säilyy.
 */
function DateInputFI({ value, onChange, testId }) {
  const isoToFi = (iso) => {
    if (!iso) return "";
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return "";
    return `${m[3]}.${m[2]}.${m[1]}`;
  };
  const fiToIso = (fi) => {
    const s = String(fi || "").trim();
    if (!s) return "";
    const parts = s.split(/[.\-/,\s]+/).filter(Boolean);
    let d, mo, y;
    if (parts.length === 3) {
      [d, mo, y] = parts;
    } else if (parts.length === 1 && /^\d{8}$/.test(parts[0])) {
      d = parts[0].slice(0, 2); mo = parts[0].slice(2, 4); y = parts[0].slice(4);
    } else if (parts.length === 2) {
      // pp.kk oletetaan kuluvaksi vuodeksi
      [d, mo] = parts; y = String(new Date().getFullYear());
    } else {
      return "";
    }
    d = parseInt(d, 10); mo = parseInt(mo, 10); y = parseInt(y, 10);
    if (Number.isNaN(d) || Number.isNaN(mo) || Number.isNaN(y)) return "";
    if (y < 100) y += 2000;
    if (mo < 1 || mo > 12) return "";
    if (d < 1 || d > 31) return "";
    return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  };

  const [text, setText] = useState(isoToFi(value));
  useEffect(() => { setText(isoToFi(value)); }, [value]);

  const commit = () => {
    const iso = fiToIso(text);
    setText(isoToFi(iso));
    if (iso !== (value || "")) onChange(iso);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      className="input-base tabular-nums"
      placeholder="pp.kk.vvvv"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
      maxLength={10}
      data-testid={testId}
    />
  );
}
